/**
 * Polymarket US trade economics — fee engine + the client-approved
 * Risk / To Win / Juice convention for the `polymarket_us` venue.
 *
 * Lockstep pair: src/lib/pricing/polyUsEconomics.ts and
 * relay/src/services/polyUsEconomics.ts are byte-identical (the relay's
 * tsconfig rootDir is `src`, so the two trees cannot import each other);
 * the frontend test reads both from disk and fails if they drift. No
 * imports, on purpose — that is what keeps them identical.
 *
 * Fee (docs.polymarket.us/fees): `Fee = Θ × C × p × (1 − p)`; taker Θ 0.06
 * (0.0695 from 2026-09-16), maker Θ −0.0125 (a REBATE, paid to the maker);
 * "All fees and rebates are rounded to the nearest $0.01 using banker's
 * rounding (round half to even)." `p` is the trade price in YES terms
 * (the venue quotes every price on the YES side).
 *
 * Economics convention (docs/accounting/README.md, unchanged from intl):
 *   cleanCost = contracts × price      // fee-exclusive basis
 *   risk      = cleanCost + fee        // fee-INCLUSIVE, all money out the door
 *   toWin     = contracts × $1 − cleanCost
 *   juice     = American odds of laying `risk` to clear `toWin`
 * The opening fee is counted exactly once, in the juice. Because the US
 * venue pays makers a rebate, `fee` can be NEGATIVE: risk drops below the
 * clean cost and the juice comes out better than the vig-free line. That is
 * the venue crediting cash at execution, not an accounting trick.
 */

export interface PolyUsEconomics {
  /** Fee-inclusive cents committed at entry (cleanCost + fee; fee may be negative). */
  riskCents: number | null;
  /** Fee-exclusive cost basis: contracts × side price. */
  cleanCostCents: number | null;
  /** Fee-free winnings on a favorable settlement: contracts × 100 − cleanCost. */
  toWinCents: number | null;
  /** American odds of laying `risk` to clear `toWin`, rounded to `decimals`. */
  juiceAmerican: number | null;
  /** Formatted juice ("-104.78" / "+225.44"), em-dash when undefined. */
  juice: string;
}

const NULL_ECONOMICS: PolyUsEconomics = {
  riskCents: null,
  cleanCostCents: null,
  toWinCents: null,
  juiceAmerican: null,
  juice: '—',
};

/** Taker coefficient in force before 2026-09-16 (docs: effective 2026-07-01). */
export const POLY_US_TAKER_COEFFICIENT_UNTIL_2026_09_16 = 0.06;
/** Taker coefficient effective 2026-09-16 (docs: "increases to 0.0695"). */
export const POLY_US_TAKER_COEFFICIENT = 0.0695;
/** Maker coefficient — negative = rebate credited to the maker. */
export const POLY_US_MAKER_COEFFICIENT = -0.0125;

/**
 * PREVIEW-ONLY fallback coefficient. The authoritative value is the market
 * object's `feeCoefficient`; use these constants only when that field is
 * unavailable, and never persist a fee derived from them as venue evidence.
 */
export function polyUsFallbackCoefficient(isTaker: boolean): number {
  return isTaker ? POLY_US_TAKER_COEFFICIENT : POLY_US_MAKER_COEFFICIENT;
}

/**
 * Round to the nearest cent, half to even (banker's), on a dollar amount.
 * Float products like 0.0125 × 1000 × 0.3 × 0.7 land a hair off the exact
 * half (2.6249999…), so the half test carries an epsilon instead of an
 * equality — the docs' examples are exact halves and must round even.
 */
export function roundHalfEvenCents(dollars: number): number {
  const scaled = dollars * 100;
  const floor = Math.floor(scaled);
  const frac = scaled - floor;
  if (Math.abs(frac - 0.5) < 1e-7) {
    return (floor % 2 === 0 ? floor : floor + 1) / 100;
  }
  return Math.round(scaled) / 100;
}

/**
 * Fee in dollars for one execution: `coefficient × contracts × p × (1 − p)`,
 * banker's-rounded to the cent. Negative coefficient → negative (rebate).
 * `priceUsd` is the YES-side trade price in [0, 1].
 */
export function polyUsFeeUsd(args: {
  contracts: number;
  priceUsd: number;
  /** The market's `feeCoefficient` (taker) or the maker coefficient. Required. */
  coefficient: number;
}): number {
  const { contracts, priceUsd, coefficient } = args;
  if (
    !Number.isFinite(contracts) ||
    contracts <= 0 ||
    !Number.isFinite(priceUsd) ||
    priceUsd < 0 ||
    priceUsd > 1 ||
    !Number.isFinite(coefficient)
  ) {
    return 0;
  }
  const raw = coefficient * contracts * priceUsd * (1 - priceUsd);
  const rounded = roundHalfEvenCents(Math.abs(raw));
  return raw < 0 ? -rounded : rounded;
}

/**
 * Ticket preview fee in cents (signed; negative = maker rebate). Uses the
 * market's `feeCoefficient` when supplied, else the preview-only constant
 * for the role. `priceCents` is the YES-side price.
 */
export function polyUsPreviewFee(args: {
  contracts: number;
  priceCents: number;
  isTaker: boolean;
  feeCoefficient?: number | null;
}): number {
  const { contracts, priceCents, isTaker, feeCoefficient } = args;
  const takerCoefficient =
    feeCoefficient != null && Number.isFinite(feeCoefficient)
      ? feeCoefficient
      : POLY_US_TAKER_COEFFICIENT;
  const coefficient = isTaker ? takerCoefficient : POLY_US_MAKER_COEFFICIENT;
  const usd = polyUsFeeUsd({ contracts, priceUsd: priceCents / 100, coefficient });
  return Math.round(usd * 100);
}

/**
 * Net buying-power hit for a short (selling YES / buying NO): the venue
 * locks $1.00 per contract and credits the sale price, so the reservation
 * is `contracts × (100 − price)` cents. `priceCents` is the YES price sold.
 */
export function polyUsShortMarginCents(args: { contracts: number; priceCents: number }): number {
  const { contracts, priceCents } = args;
  if (!Number.isFinite(contracts) || contracts <= 0) return 0;
  if (!Number.isFinite(priceCents) || priceCents < 0 || priceCents > 100) return 0;
  return contracts * (100 - priceCents);
}

/** Implied-price American odds: boundaries → null, even money → +100. */
function riskToWinOdds(risk: number, toWin: number, decimals: number): number | null {
  const cents = (100 * risk) / (risk + toWin);
  if (!Number.isFinite(cents) || cents <= 0 || cents >= 100) return null;
  const factor = 10 ** decimals;
  const round = (v: number) => Math.round(v * factor) / factor;
  if (cents < 50) return round((100 * (100 - cents)) / cents);
  if (cents > 50) return round((-100 * cents) / (100 - cents));
  return 100;
}

/**
 * Risk / To Win / Juice for one Polymarket US entry.
 *
 * `priceCents` is the venue's YES-side price (its convention for every
 * order and fill). `side` picks which side the trader HOLDS: a `'no'` entry
 * at YES price p costs `100 − p` per contract. Doing the inversion here keeps
 * the "price is always YES" trap in one place.
 *
 * `feesCents` is the signed venue fee for the entry: positive taker fee,
 * negative maker rebate, `null` = unknown → all-null economics (fail closed,
 * never a fabricated line). 0 = known-free.
 */
export function polyUsEconomics(args: {
  contracts: number;
  priceCents: number;
  feesCents: number | null;
  side: 'yes' | 'no';
  decimals?: number;
}): PolyUsEconomics {
  const { contracts, priceCents, feesCents, side, decimals = 2 } = args;
  if (
    !Number.isFinite(contracts) ||
    contracts <= 0 ||
    !Number.isFinite(priceCents) ||
    priceCents <= 0 ||
    priceCents >= 100 ||
    feesCents == null ||
    !Number.isFinite(feesCents)
  ) {
    return NULL_ECONOMICS;
  }
  const sidePriceCents = side === 'yes' ? priceCents : 100 - priceCents;
  const cleanCostCents = contracts * sidePriceCents;
  const riskCents = cleanCostCents + feesCents;
  const toWinCents = contracts * 100 - cleanCostCents;
  if (riskCents <= 0 || toWinCents <= 0) return NULL_ECONOMICS;
  const juiceAmerican = riskToWinOdds(riskCents, toWinCents, decimals);
  return {
    riskCents,
    cleanCostCents,
    toWinCents,
    juiceAmerican,
    juice:
      juiceAmerican == null
        ? '—'
        : `${juiceAmerican > 0 ? '+' : ''}${juiceAmerican.toFixed(decimals)}`,
  };
}
